import axiosInstance from "core/api/axiosInstance";

// ================================================
// CONFIGURACIÓN
// ================================================
const CONFIG = {
  TIMEOUTS: {
    DEFAULT: 15000,
    HEAVY_QUERY: 60000,
  },
};

// ================================================
// HELPERS DE AUTENTICACIÓN
// ================================================
const getAuthInfo = () => {
  try {
    const token = localStorage.getItem("token");
    return { hasToken: !!token, token: token || null };
  } catch (error) {
    return { hasToken: false, token: null };
  }
};

const validateAuth = () => {
  const authInfo = getAuthInfo();
  if (!authInfo.hasToken) {
    throw new Error("No hay token de autenticación. Por favor inicie sesión.");
  }
  return authInfo;
};

const createHeaders = (additionalHeaders = {}) => {
  const authInfo = getAuthInfo();
  return {
    "Content-Type": "application/json",
    Accept: "application/json",
    Authorization: `Bearer ${authInfo.token}`,
    ...additionalHeaders,
  };
};

// ================================================
// SERVICIOS
// ================================================

/**
 * Obtiene todas las concesiones de tipo PEAJE desde el backend.
 * Retorna la data CRUDA (sin aplanar) para facilitar la edición directa.
 */
export const getConcesionesService = async () => {
  try {
    validateAuth();

    const config = {
      headers: createHeaders(),
      timeout: CONFIG.TIMEOUTS.DEFAULT,
    };

    const response = await axiosInstance.get("/api/commerce/get-all", config);
    const { data } = response;

    if (!Array.isArray(data)) {
      return { success: false, error: "Formato de respuesta inválido" };
    }

    // Filtrar solo type === "PEAJE"
    const concesiones = data
      .filter((item) => item.type === "PEAJE")
      .map((item) => ({
        ...item,
        rules: item.rules || {},
      }));

    return {
      success: true,
      data: concesiones,
      count: concesiones.length,
      message: "Concesiones obtenidas exitosamente",
    };
  } catch (error) {
    console.error("Error en getConcesionesService:", error);
    let errorMessage = "Error al cargar concesiones";
    if (error.response) {
      errorMessage = error.response.data?.message || `Error ${error.response.status}`;
    } else if (error.request) {
      errorMessage = "No se pudo conectar con el servidor";
    } else {
      errorMessage = error.message;
    }
    return { success: false, error: errorMessage, details: error.response?.data };
  }
};

/**
 * Actualiza la política de pago de una concesión.
 * Endpoint: PUT /dispersion/politicas/{code}
 *
 * @param {string|number} code  Código de la concesión (ej: "10188")
 * @param {Object} politicaPago Objeto con la estructura plana de politica_pago
 *                              (modalidad, frecuencia_lv, dias_traslado_lv, etc.)
 */
export const updatePoliticaPagoService = async (code, politicaPago) => {
  try {
    validateAuth();

    if (!code) {
      throw new Error("El código de la concesión es requerido");
    }

    // Construimos el payload EXACTO que espera el backend (según el curl)
    const payload = {
      modalidad: politicaPago.modalidad || "CONSOLIDADO",
      descripcion_modalidad:
        politicaPago.descripcion_modalidad ??
        (politicaPago.modalidad === "CONSOLIDADO"
          ? "Un solo Pago por el total de todos los peajes"
          : "Pago Independiente por el valor de cada peaje"),
      frecuencia_lv: politicaPago.frecuencia_lv || "",
      dias_habiles_lv: Number(politicaPago.dias_habiles_lv) || 0,
      dias_traslado_lv: politicaPago.dias_traslado_lv || "",
      frecuencia_fds: politicaPago.frecuencia_fds || "",
      dias_habiles_fds: Number(politicaPago.dias_habiles_fds) || 0,
      dias_traslado_fds: politicaPago.dias_traslado_fds || "",
      estaciones_peajes: Array.isArray(politicaPago.estaciones_peajes)
        ? politicaPago.estaciones_peajes.map((est) => ({
            peaje: est.peaje,
            hora_inicio: est.hora_inicio,
            hora_fin: est.hora_fin,
            pago_independiente: !!est.pago_independiente,
          }))
        : [],
    };

    const config = {
      headers: createHeaders(),
      timeout: CONFIG.TIMEOUTS.DEFAULT,
    };

    const url = `/api/py/dispersion/politicas/${encodeURIComponent(String(code).trim())}`;
    const response = await axiosInstance.put(url, payload, config);

    return {
      success: true,
      data: response.data?.data || response.data || payload,
      message: response.data?.message || "Política de pago actualizada correctamente",
    };
  } catch (error) {
    console.error("Error en updatePoliticaPagoService:", error);

    let errorMessage = "Error al actualizar la política de pago";
    if (error.response) {
      errorMessage =
        error.response.data?.message ||
        error.response.data?.error ||
        `Error ${error.response.status}`;
    } else if (error.request) {
      errorMessage = "No se pudo conectar con el servidor";
    } else {
      errorMessage = error.message;
    }

    return {
      success: false,
      error: errorMessage,
      details: error.response?.data,
    };
  }
};
